import React from "react";
import useEditService from "../hooks/useEditService";
import useUploadFiles from "../../../hooks/useUploadFiles";

// components
import Modal from "../../../component/ui/modal";
import Tab from "../../../component/ui/tabs/Tabs";
import Upload from "../../../component/modals/upload";
import TextInput from "../../../component/element/forms/TextInput";
import TextMultipleInput from "../../../component/element/forms/TextMultipleInput";
import ItemAction from "../../../component/ui/action/ItemAction";

// constants

// types and state
import {
  create_packages,
  delete_package,
  edit_service,
  get_service,
  update_package,
} from "../../../services/apis/services";
import { useAppDispatch, useAppSelector } from "../../../store";
import { IEditServiceError } from "../../../store/types/service";
import RichTextEditor from "../../../component/element/forms/RichTextEditor";
import SelectOption from "../../../component/element/forms/SelectOption";
import { get_countries } from "../../../services/apis/locations";
import { ISimpleData } from "../../../store/types/misc";

type IEditService = {
  id: number | string;
  show: boolean;
  onCancel: () => void;
};

const EditService: React.FC<IEditService> = ({ id, show, onCancel }) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ service }) => service.loading);
  const { data, err, dataSet, errSet, packageIds, packageIdsSet } =
    useEditService(id);
  const { loader, uploadFile } = useUploadFiles();
  const [deletingPackageId, setDeletingPackageId] = React.useState<
    number | null
  >(null);
  const [savingPackage, setSavingPackage] = React.useState(false);
  const [editingPackageId, setEditingPackageId] = React.useState<number | null>(
    null,
  );
  const [editingPackageName, setEditingPackageName] = React.useState("");
  const countries = useAppSelector(({ locations }) => locations.countries);

  React.useEffect(() => {
    if (show && countries.length === 0) void get_countries(dispatch);
  }, [countries.length, dispatch, show]);

  const countryOptions: ISimpleData[] = countries.map((country) => ({
    id: String(country.id),
    name: country.name,
  }));

  const onLoad = React.useCallback(() => {
    if (id) get_service(dispatch, id);
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  const onsubmit = async () => {
    const errors: IEditServiceError = {
      ...err,
      id: data.id ? "" : "error: this field is required",
      country_id: data.country_id ? "" : "error: this field is required",
      name: data.name.trim() ? "" : "error: this field is required",
      description: data.description.trim()
        ? ""
        : "error: this field is required",
      price: data.price.trim() ? "" : "error: this field is required",
    };
    errSet(errors);
    if (
      !data.id ||
      !data.country_id ||
      !data.name.trim() ||
      !data.description.trim() ||
      !data.price.trim()
    ) {
      return;
    }
    const response = await edit_service(data, dispatch);
    if (response) onCancel();
  };

  return (
    <Modal
      title="Edit Service"
      open={show}
      onClose={onCancel}
      onOkClick={onsubmit}
      isLoading={loading}
    >
      <div className="space-y-6">
        <div>
          <Upload
            title="Upload Image"
            name="image"
            status={err.image}
            loading={loader}
            completed={!!data.image}
            imgPreview={data.image}
            onChange={async (fileOrFiles) => {
              const paths = await uploadFile(fileOrFiles as File, "services");
              if (paths.length > 0) {
                const img_path = paths[0]?.base_url + "/" + paths[0]?.path;
                dataSet((prev) => ({ ...prev, image: img_path }));
                errSet((prev) => ({ ...prev, image: "" }));
              }
            }}
          />
        </div>

        <div>
          <SelectOption
            items={countryOptions}
            label="Country"
            placeholder="Select country"
            value={data.country_id || ""}
            onChange={(countryId) => {
              dataSet((prev) => ({
                ...prev,
                country_id: countryId ? Number(countryId) : 0,
              }));
              errSet((prev) => ({ ...prev, country_id: "" }));
            }}
          />
          {err.country_id && (
            <p className="mt-1 text-sm text-red-500">{err.country_id}</p>
          )}
        </div>

        <div>
          <TextInput
            value={data.name}
            label="Name"
            name="name"
            placeholder="Service Name"
            type="text"
            status={err.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { name, value } = e.target;
              dataSet((prev) => ({ ...prev, [name]: value }));
              errSet((prev) => ({ ...prev, [name]: "" }));
            }}
          />
        </div>

        <div>
          <TextInput
            value={data.price}
            label="Price"
            name="price"
            placeholder="Service Price"
            type="number"
            status={err.price}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              dataSet((prev) => ({ ...prev, price: value }));
              errSet((prev) => ({ ...prev, price: "" }));
            }}
          />
        </div>

        <div>
          <div className="mb-6">
            <TextMultipleInput
              label="Packages"
              onChange={(res) => {
                const packageName = res.trim();
                if (!packageName || !data.id || savingPackage) return;
                setSavingPackage(true);
                void create_packages(dispatch, data.id, packageName)
                  .then((createdPackage) => {
                    if (!createdPackage) return;
                    dataSet((prev) => ({
                      ...prev,
                      packages: [...prev.packages, createdPackage.name],
                    }));
                    packageIdsSet((prev) => [...prev, createdPackage.id]);
                    errSet((prev) => ({ ...prev, packages: "" }));
                  })
                  .finally(() => setSavingPackage(false));
              }}
            />
            {savingPackage && (
              <p className="mt-1 text-sm text-gray-500">Adding package...</p>
            )}
          </div>
          <div>
            {data.packages.length > 0 && (
              <div className="grid gap-2">
                {data.packages.map((item, index) => {
                  const packageId = packageIds[index];
                  const isEditing = packageId === editingPackageId;
                  return (
                    <div
                      key={packageId ?? `${item}-${index}`}
                      className="flex items-center justify-between gap-3 border p-2 rounded-lg"
                    >
                      {isEditing ? (
                        <div className="flex flex-1 items-center gap-2">
                          <input
                            value={editingPackageName}
                            onChange={(event) =>
                              setEditingPackageName(event.target.value)
                            }
                            className="min-w-0 flex-1 rounded border px-2 py-1"
                            aria-label="Package name"
                          />
                          <button
                            type="button"
                            disabled={!editingPackageName.trim() || savingPackage}
                            className="text-sm text-blue-700 disabled:opacity-50"
                            onClick={async () => {
                              if (packageId === null || packageId === undefined)
                                return;
                              setSavingPackage(true);
                              try {
                                const updatedPackage = await update_package(
                                  dispatch,
                                  data.id,
                                  packageId,
                                  editingPackageName.trim(),
                                );
                                if (updatedPackage) {
                                  dataSet((prev) => ({
                                    ...prev,
                                    packages: prev.packages.map(
                                      (packageName, itemIndex) =>
                                        itemIndex === index
                                          ? updatedPackage.name
                                          : packageName,
                                    ),
                                  }));
                                  setEditingPackageId(null);
                                }
                              } finally {
                                setSavingPackage(false);
                              }
                            }}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="text-sm text-gray-600"
                            onClick={() => setEditingPackageId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <Tab value={item} />
                          <ItemAction
                            show_view={false}
                            show_edit={packageId !== null}
                            delete_loading={
                              packageId !== null &&
                              deletingPackageId === packageId
                            }
                            onView={() => null}
                            onEdit={() => {
                              if (packageId === null) return;
                              setEditingPackageId(packageId);
                              setEditingPackageName(item);
                            }}
                            onDelete={async () => {
                              if (packageId === null || packageId === undefined)
                                return;

                              setDeletingPackageId(packageId);
                              try {
                                const deleted = await delete_package(
                                  dispatch,
                                  data.id,
                                  packageId,
                                );
                                if (deleted) {
                                  dataSet((prev) => ({
                                    ...prev,
                                    packages: prev.packages.filter(
                                      (_, itemIndex) => itemIndex !== index,
                                    ),
                                  }));
                                  packageIdsSet((prev) =>
                                    prev.filter(
                                      (_, itemIndex) => itemIndex !== index,
                                    ),
                                  );
                                }
                              } finally {
                                setDeletingPackageId(null);
                              }
                            }}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <RichTextEditor
          label="Description"
          value={data.description}
          error={err.description}
          onChange={(value) => {
            dataSet((prev) => ({ ...prev, description: value }));
            errSet((prev) => ({ ...prev, description: "" }));
          }}
        />
      </div>
    </Modal>
  );
};

export default EditService;
